import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RatesFilterStateService, FilterStateSlot } from '../../state/rates-filter-state.service';
import { POPOVER_CONTEXT, PickerComponent } from './picker.types';
import { pickerRegistry } from './picker-registry';

/**
 * Menu-axis Phase 3e.2 — FlatChecklistPickerComponent.
 *
 * Generic flat checkbox list. Used by 8 dimensions:
 *   index · combo · surr · mktg · strat · am-best · riders · prem-type
 *
 * Live-apply per D9: every click writes through `setSlotField` AND
 * calls `requestApplyFilters()`. No Apply/Clear footer (just a "clear"
 * link). The search input filters the OPTION LIST -- it does not
 * filter the rate grid.
 */
@Component({
  selector: 'app-flat-checklist-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './flat-checklist-picker.component.html',
  styleUrl: './flat-checklist-picker.component.scss',
})
export class FlatChecklistPickerComponent implements PickerComponent {
  protected readonly ctx = inject(POPOVER_CONTEXT);
  protected readonly filterState = inject(RatesFilterStateService);

  protected readonly searchText = signal('');

  /** All options for this dimension (closure provided by the rail). */
  protected readonly allOptions = computed<string[]>(() => this.ctx.optionsFn());

  /** Phase 10.3.2 (spec P11) — hide the per-picker search input when
   *  the option list is short enough to scan visually. Threshold is
   *  >8 (i.e. 9+ options shows search). */
  protected readonly showSearch = computed<boolean>(() => this.allOptions().length > 8);

  /** Options visible after the search filter. */
  protected readonly filteredOptions = computed<string[]>(() => {
    const q = this.searchText().toLowerCase().trim();
    if (!q) return this.allOptions();
    return this.allOptions().filter((o) => o.toLowerCase().includes(q));
  });

  /** Currently selected values from the active slot. */
  protected readonly selected = computed<string[]>(() => {
    this.filterState.version();
    const slot = this.filterState.activeSlot();
    const value = slot[this.ctx.field];
    return Array.isArray(value) ? (value as string[]) : [];
  });

  /** Toggle one option in the selection. Live-applies. */
  toggle(opt: string): void {
    const current = this.selected();
    const next = current.includes(opt)
      ? current.filter((v) => v !== opt)
      : [...current, opt];
    this.filterState.setSlotField(
      this.ctx.field as keyof FilterStateSlot,
      next as FilterStateSlot[keyof FilterStateSlot],
    );
    this.filterState.requestApplyFilters();
  }

  /** Clear all selections for this dimension (rest of dimension chip
   *  goes away after the next requestApplyFilters tick). */
  clearAll(): void {
    this.filterState.clearAppliedDimension(this.ctx.dimensionKey);
    this.filterState.requestApplyFilters();
  }

  /** Search input two-way binding helper. */
  protected onSearchInput(value: string): void {
    this.searchText.set(value);
  }
}

// Register entries for the 8 FlatChecklist dimensions on module load.
// Defers picker-registry from "empty in 3e.1" → "populated in 3e.2".
pickerRegistry['index']       = { component: FlatChecklistPickerComponent, label: 'Index',           field: 'selectedIndexes',              group: 'Strategy' };
pickerRegistry['combo']       = { component: FlatChecklistPickerComponent, label: 'Strategy combo',  field: 'selectedStrategyCombos',       group: 'Strategy' };
pickerRegistry['surr']        = { component: FlatChecklistPickerComponent, label: 'Surrender Years', field: 'selectedSurrYears',            group: 'Features' };
pickerRegistry['mktg']        = { component: FlatChecklistPickerComponent, label: 'Mktg Name',       field: 'selectedCreditingStrategies',  group: 'Strategy' };
pickerRegistry['strat']       = { component: FlatChecklistPickerComponent, label: 'Strategy Name',   field: 'selectedStrategyNames',        group: 'Strategy' };
pickerRegistry['am-best']     = { component: FlatChecklistPickerComponent, label: 'A.M. Best',       field: 'selectedAmBest',               group: 'Carrier' };
pickerRegistry['riders']      = { component: FlatChecklistPickerComponent, label: 'Riders',          field: 'selectedRiders',               group: 'Features' };
pickerRegistry['prem-type']   = { component: FlatChecklistPickerComponent, label: 'Premium Type',    field: 'selectedPremiumTypes',         group: 'Features' };
// RILA/VA filter block (2026-06-05) — four multi-select dimensions reusing
// the FlatChecklist picker. Tab-applicability gating is enforced by
// rates-rail.component.html: the @if(activeTab() === 'RILA' || === 'VA')
// wrapper hides them on FA/IA.
pickerRegistry['buffer-level']     = { component: FlatChecklistPickerComponent, label: 'Buffer Level',     field: 'selectedBufferLevels',     group: 'Strategy' };
pickerRegistry['floor-level']      = { component: FlatChecklistPickerComponent, label: 'Floor Level',      field: 'selectedFloorLevels',      group: 'Strategy' };
pickerRegistry['segment-duration'] = { component: FlatChecklistPickerComponent, label: 'Segment Duration', field: 'selectedSegmentDurations', group: 'Strategy' };
pickerRegistry['rate-type']        = { component: FlatChecklistPickerComponent, label: 'Rate Type',        field: 'selectedRateTypes',        group: 'Strategy' };
